import { BasePlugin, PluginRegistry } from '@embedpdf/core';
import {
  RenderCapability,
  RenderPageOptions,
  RenderPageRectOptions,
  RenderPluginConfig,
  RenderScope,
} from './types';

/**
 * Render Plugin - Simplified version that relies on core state for refresh tracking
 *
 * Key insight: Page refresh tracking is in DocumentState.pageRefreshVersions
 * This allows ANY plugin to observe page refreshes, not just the render plugin.
 */
export class RenderPlugin extends BasePlugin<RenderPluginConfig, RenderCapability> {
  static readonly id = 'render' as const;

  private config: RenderPluginConfig;

  constructor(id: string, registry: PluginRegistry, config: RenderPluginConfig) {
    super(id, registry);
    this.config = config;
  }

  // No onDocumentLoadingStarted or onDocumentClosed needed!

  protected buildCapability(): RenderCapability {
    return {
      // Active document operations
      renderPage: (options: RenderPageOptions) => this.renderPage(options),
      renderPageRect: (options: RenderPageRectOptions) => this.renderPageRect(options),
      renderPageRaw: (options: RenderPageOptions) => this.renderPageRaw(options),
      renderPageRectRaw: (options: RenderPageRectOptions) => this.renderPageRectRaw(options),

      // Document-scoped operations
      forDocument: (documentId: string) => this.createRenderScope(documentId),
    };
  }

  // ─────────────────────────────────────────────────────────
  // Document Scoping
  // ─────────────────────────────────────────────────────────

  private createRenderScope(documentId: string): RenderScope {
    return {
      renderPage: (options: RenderPageOptions) => this.renderPage(options, documentId),
      renderPageRect: (options: RenderPageRectOptions) => this.renderPageRect(options, documentId),
      renderPageRaw: (options: RenderPageOptions) => this.renderPageRaw(options, documentId),
      renderPageRectRaw: (options: RenderPageRectOptions) =>
        this.renderPageRectRaw(options, documentId),
    };
  }

  // ─────────────────────────────────────────────────────────
  // Core Operations
  // ─────────────────────────────────────────────────────────

  private renderPage({ pageIndex, options }: RenderPageOptions, documentId?: string) {
    const id = documentId ?? this.getActiveDocumentId();
    const coreDoc = this.coreState.core.documents[id];

    if (!coreDoc?.document) {
      throw new Error(`Document ${id} not loaded`);
    }

    const page = coreDoc.document.pages.find((p) => p.index === pageIndex);
    if (!page) {
      throw new Error(`Page ${pageIndex} not found in document ${id}`);
    }

    const mergedOptions = {
      ...(options ?? {}),
      withForms: options?.withForms ?? this.config.withForms ?? false,
      withAnnotations: options?.withAnnotations ?? this.config.withAnnotations ?? false,
      imageType: options?.imageType ?? this.config.defaultImageType ?? 'image/png',
      imageQuality: options?.imageQuality ?? this.config.defaultImageQuality ?? 0.92,
    };

    return this.engine.renderPage(coreDoc.document, page, mergedOptions);
  }

  private renderPageRect({ pageIndex, rect, options }: RenderPageRectOptions, documentId?: string) {
    const id = documentId ?? this.getActiveDocumentId();
    const coreDoc = this.coreState.core.documents[id];

    if (!coreDoc?.document) {
      throw new Error(`Document ${id} not loaded`);
    }

    const page = coreDoc.document.pages.find((p) => p.index === pageIndex);
    if (!page) {
      throw new Error(`Page ${pageIndex} not found in document ${id}`);
    }

    const mergedOptions = {
      ...(options ?? {}),
      withForms: options?.withForms ?? this.config.withForms ?? false,
      withAnnotations: options?.withAnnotations ?? this.config.withAnnotations ?? false,
      imageType: options?.imageType ?? this.config.defaultImageType ?? 'image/png',
      imageQuality: options?.imageQuality ?? this.config.defaultImageQuality ?? 0.92,
    };

    return this.engine.renderPageRect(coreDoc.document, page, rect, mergedOptions);
  }

  // ─────────────────────────────────────────────────────────
  // Raw Rendering (returns ImageDataLike, skips encoding)
  // ─────────────────────────────────────────────────────────

  private renderPageRaw({ pageIndex, options }: RenderPageOptions, documentId?: string) {
    const id = documentId ?? this.getActiveDocumentId();
    const coreDoc = this.coreState.core.documents[id];

    if (!coreDoc?.document) {
      throw new Error(`Document ${id} not loaded`);
    }

    const page = coreDoc.document.pages.find((p) => p.index === pageIndex);
    if (!page) {
      throw new Error(`Page ${pageIndex} not found in document ${id}`);
    }

    const mergedOptions = {
      ...(options ?? {}),
      withForms: options?.withForms ?? this.config.withForms ?? false,
      withAnnotations: options?.withAnnotations ?? this.config.withAnnotations ?? false,
    };

    return this.engine.renderPageRaw(coreDoc.document, page, mergedOptions);
  }

  private renderPageRectRaw(
    { pageIndex, rect, options }: RenderPageRectOptions,
    documentId?: string,
  ) {
    const id = documentId ?? this.getActiveDocumentId();
    const coreDoc = this.coreState.core.documents[id];

    if (!coreDoc?.document) {
      throw new Error(`Document ${id} not loaded`);
    }

    const page = coreDoc.document.pages.find((p) => p.index === pageIndex);
    if (!page) {
      throw new Error(`Page ${pageIndex} not found in document ${id}`);
    }

    const mergedOptions = {
      ...(options ?? {}),
      withForms: options?.withForms ?? this.config.withForms ?? false,
      withAnnotations: options?.withAnnotations ?? this.config.withAnnotations ?? false,
    };

    return this.engine.renderPageRectRaw(coreDoc.document, page, rect, mergedOptions);
  }

  // ─────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────

  async initialize(_config: RenderPluginConfig): Promise<void> {
    this.logger.info('RenderPlugin', 'Initialize', 'Render plugin initialized');
  }

  async destroy(): Promise<void> {
    super.destroy();
  }
}
